// src/HeroSearchFilter.tsx
import React, { useState, useEffect } from 'react';
import { MARCAS_CARRO, MARCAS_MOTO, getModelosPorMarca } from './data/productos';

export interface FiltrosBusqueda {
    categoria: 'vehiculo' | 'repuestos';
    tipoVehiculo: 'todos' | 'carro' | 'moto';
    marca: string;
    modelo: string;
    condicion: 'todos' | 'nuevo' | 'usado';
    queryTexto: string;
}

interface HeroSearchFilterProps {
    onSearchChange: (filtros: FiltrosBusqueda) => void; 
}

const HeroSearchFilter: React.FC<HeroSearchFilterProps> = ({ onSearchChange }) => {
    
    const [mainTab, setMainTab] = useState<'vehiculo' | 'repuestos'>('vehiculo');
    
    // Estados para Vehículos
    const [condicion, setCondicion] = useState<'todos' | 'nuevo' | 'usado'>('todos');
    const [tipoVehiculo, setTipoVehiculo] = useState<'todos' | 'carro' | 'moto'>('todos');
    const [marca, setMarca] = useState<string>('todas');
    const [modelo, setModelo] = useState<string>('todos');

    // Estados para Repuestos
    const [queryRepuesto, setQueryRepuesto] = useState('');
    const [tipoRepuesto, setTipoRepuesto] = useState<'ambos' | 'carro' | 'moto'>('ambos');

    // Listas dinámicas
    const [listaMarcas, setListaMarcas] = useState<string[]>([]);
    const [listaModelos, setListaModelos] = useState<string[]>([]);

    useEffect(() => {
        if (tipoVehiculo === 'carro') {
            setListaMarcas(MARCAS_CARRO);
        } else if (tipoVehiculo === 'moto') {
            setListaMarcas(MARCAS_MOTO);
        } else {
            setListaMarcas([...MARCAS_CARRO, ...MARCAS_MOTO].sort());
        }
        setMarca('todas'); 
    }, [tipoVehiculo]);

    useEffect(() => {
        if (marca !== 'todas') {
            setListaModelos(getModelosPorMarca(marca));
        } else {
            setListaModelos([]);
        }
        setModelo('todos'); 
    }, [marca]);

    const handleSearch = () => {
        const filtros: FiltrosBusqueda = {
            categoria: mainTab,
            tipoVehiculo: mainTab === 'vehiculo' ? tipoVehiculo : 'todos',
            condicion: mainTab === 'vehiculo' ? condicion : 'todos',
            marca: marca !== 'todas' ? marca : '',
            modelo: modelo !== 'todos' ? modelo : '',
            queryTexto: mainTab === 'repuestos' ? queryRepuesto : ''
        };
        
        onSearchChange(filtros);
    };

    const tabActive = "bg-yellow-600 text-white border-b-0";
    const tabInactive = "bg-black/60 text-gray-300 hover:bg-black/80 hover:text-white border-b-2 border-transparent";
    const selectClass = "w-full p-3 bg-white text-gray-900 font-semibold border-r-8 border-transparent focus:ring-2 focus:ring-yellow-500 outline-none h-12";
    const labelClass = "block text-xs font-bold text-gray-300 uppercase tracking-wider mb-1 ml-1";

    return (
        <div className="w-full max-w-7xl mx-auto px-4 relative z-20">
            <div className="bg-black/90 backdrop-blur-md shadow-2xl border border-gray-700 overflow-hidden mt-8">
                
                {/* TABS */}
                <div className="flex">
                    <button 
                        onClick={() => setMainTab('vehiculo')}
                        className={`flex-1 py-4 text-center text-sm sm:text-lg font-bold uppercase tracking-wide transition-all duration-200 ${mainTab === 'vehiculo' ? tabActive : tabInactive}`}
                    >
                        Vehículos (Carros & Motos)
                    </button>
                    <button 
                        onClick={() => setMainTab('repuestos')}
                        className={`flex-1 py-4 text-center text-sm sm:text-lg font-bold uppercase tracking-wide transition-all duration-200 ${mainTab === 'repuestos' ? tabActive : tabInactive}`}
                    >
                        Repuestos & Accesorios
                    </button>
                </div>

                <div className="p-6 md:p-8">
                    {/* VEHÍCULOS */}
                    {mainTab === 'vehiculo' && (
                        <div className="animate-fadeIn">
                            <div className="flex flex-wrap gap-6 mb-6 border-b border-gray-700 pb-4">
                                {['todos', 'nuevo', 'usado'].map((c) => (
                                    <label key={c} className="flex items-center cursor-pointer group">
                                        <div className="relative flex items-center justify-center">
                                            <input 
                                                type="radio" name="condicion" value={c} 
                                                checked={condicion === c}
                                                onChange={() => setCondicion(c as any)}
                                                className="peer appearance-none h-5 w-5 border-2 border-gray-400 rounded-full checked:border-yellow-500 checked:bg-yellow-500 transition-all"
                                            />
                                            <div className="hidden peer-checked:block absolute w-2 h-2 bg-white rounded-full pointer-events-none"></div>
                                        </div>
                                        <span className={`ml-2 text-sm font-bold uppercase transition-colors ${condicion === c ? 'text-white' : 'text-gray-400 group-hover:text-gray-200'}`}>
                                            {c === 'todos' ? 'Cualquier Estado' : c}
                                        </span>
                                    </label>
                                ))}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                                <div>
                                    <span className={labelClass}>Tipo</span>
                                    <select value={tipoVehiculo} onChange={(e) => setTipoVehiculo(e.target.value as any)} className={selectClass}>
                                        <option value="todos">Todos</option>
                                        <option value="carro">Carros</option>
                                        <option value="moto">Motos</option>
                                    </select>
                                </div>
                                <div>
                                    <span className={labelClass}>Marca</span>
                                    <select value={marca} onChange={(e) => setMarca(e.target.value)} className={selectClass}>
                                        <option value="todas">Todas las Marcas</option>
                                        {listaMarcas.map(m => <option key={m} value={m}>{m}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <span className={labelClass}>Modelo</span>
                                    <select value={modelo} onChange={(e) => setModelo(e.target.value)} className={selectClass} disabled={listaModelos.length === 0}>
                                        <option value="todos">Todos los Modelos</option>
                                        {listaModelos.map(mod => <option key={mod} value={mod}>{mod}</option>)}
                                    </select>
                                </div>
                                <button onClick={handleSearch} className="w-full bg-yellow-600 text-white font-extrabold py-3 px-6 shadow-lg hover:bg-yellow-700 transform hover:-translate-y-0.5 transition-all duration-200 h-12 flex items-center justify-center text-lg uppercase tracking-wider">
                                    Buscar
                                </button>
                            </div>
                        </div>
                    )}

                    {/* REPUESTOS */}
                    {mainTab === 'repuestos' && (
                        <div className="animate-fadeIn">
                             <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                                <div className="md:col-span-3">
                                    <span className={labelClass}>Compatible con</span>
                                    <select value={tipoRepuesto} onChange={(e) => setTipoRepuesto(e.target.value as any)} className={selectClass}>
                                        <option value="ambos">Carros y Motos</option>
                                        <option value="carro">Solo Carros</option>
                                        <option value="moto">Solo Motos</option>
                                    </select>
                                </div>
                                <div className="md:col-span-7">
                                    <span className={labelClass}>¿Qué pieza buscas?</span>
                                    <input type="text" placeholder="Ej: Batería, Casco..." value={queryRepuesto} onChange={(e) => setQueryRepuesto(e.target.value)} className={`${selectClass} placeholder-gray-500`} />
                                </div>
                                <div className="md:col-span-2">
                                    <button onClick={handleSearch} className="w-full bg-yellow-600 text-white font-extrabold py-3 px-6 shadow-lg hover:bg-yellow-700 transform hover:-translate-y-0.5 transition-all duration-200 h-12 flex items-center justify-center text-lg uppercase">
                                        Buscar
                                    </button>
                                </div>
                            </div>
                            <div className="mt-4 flex space-x-3 text-sm text-gray-400">
                                <span className="font-bold text-yellow-500">Popular:</span>
                                <button onClick={() => setQueryRepuesto('Aceite')} className="hover:text-white underline decoration-yellow-500">Aceite</button>
                                <button onClick={() => setQueryRepuesto('Cauchos')} className="hover:text-white underline decoration-yellow-500">Cauchos</button>
                                <button onClick={() => setQueryRepuesto('Batería')} className="hover:text-white underline decoration-yellow-500">Batería</button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default HeroSearchFilter;